import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Building2, ArrowRight, ShieldCheck, Receipt, Users } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function CorporateSignup() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [f, setF] = useState({
    company_name: "",
    contact_name: "",
    billing_email: "",
    password: "",
    phone: "",
    billing_address: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (f.password.length < 8) {
      setErr("Password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/corporate/signup", f);
      toast.success(`Welcome to Delux Limo, ${f.company_name}!`);
      // Refresh auth context so the navbar reflects the new session
      try { await refresh?.(); } catch (_e) { /* refresh is best-effort */ }
      // Send the new corporate admin straight to their dashboard
      setTimeout(() => navigate("/dashboard"), 500);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Signup failed — please try again");
    } finally {
      setBusy(false);
    }
  };

  const onChange = (k) => (e) => setF({ ...f, [k]: e.target.value });

  return (
    <div className="min-h-screen bg-[#050505] text-[#F5F2EB]" data-testid="corporate-signup-page">
      {/* Header */}
      <header className="border-b border-white/5 px-6 md:px-12 py-5">
        <Link to="/" className="inline-flex items-center gap-3" data-testid="corp-signup-logo">
          <img src="/logo.png" alt="Delux Limo" className="h-9 w-auto" onError={(e) => (e.target.style.display = "none")} />
          <div>
            <div className="font-serif text-lg leading-none">Delux Limo</div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#A1A1AA] mt-1">Trans Group</div>
          </div>
        </Link>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-12 py-12 grid lg:grid-cols-5 gap-10">
        {/* Left — value props */}
        <section className="lg:col-span-2 space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 border border-[#D4AF37]/40 text-[#D4AF37] text-[11px] uppercase tracking-[0.28em]">
            <Building2 size={14}/> For Companies
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl leading-tight">
            One account for your<br/>
            <span className="text-[#D4AF37]">whole team.</span>
          </h1>
          <p className="text-[#A1A1AA] text-base leading-relaxed">
            Self-serve corporate signup — your account activates instantly. Book
            trips for executives, clients, and visiting staff, then receive a
            single consolidated invoice every month.
          </p>

          <ul className="space-y-4 pt-4">
            <li className="flex gap-3" data-testid="benefit-instant">
              <ShieldCheck className="text-[#D4AF37] flex-shrink-0 mt-1" size={20}/>
              <div>
                <div className="text-sm font-semibold uppercase tracking-[0.18em]">Instant activation</div>
                <div className="text-sm text-[#A1A1AA]">No paperwork, no approval queue. Start booking the moment you sign up.</div>
              </div>
            </li>
            <li className="flex gap-3" data-testid="benefit-team">
              <Users className="text-[#D4AF37] flex-shrink-0 mt-1" size={20}/>
              <div>
                <div className="text-sm font-semibold uppercase tracking-[0.18em]">Invite your team</div>
                <div className="text-sm text-[#A1A1AA]">Add or remove teammate emails any time from your dashboard.</div>
              </div>
            </li>
            <li className="flex gap-3" data-testid="benefit-invoice">
              <Receipt className="text-[#D4AF37] flex-shrink-0 mt-1" size={20}/>
              <div>
                <div className="text-sm font-semibold uppercase tracking-[0.18em]">Monthly consolidated invoice</div>
                <div className="text-sm text-[#A1A1AA]">One PDF, every trip itemized. Pay by card, ACH, or check.</div>
              </div>
            </li>
          </ul>

          <div className="pt-6 text-xs text-[#71717A]">
            Need a custom volume discount or NET-30 billing? Sign up first, then call <a href="tel:+14143336275" className="text-[#D4AF37] hover:underline">(414) 333-6275</a>.
          </div>
        </section>

        {/* Right — signup form */}
        <section className="lg:col-span-3">
          <form onSubmit={submit} className="border border-white/10 bg-[#0A0A0A] p-8 md:p-10 space-y-6" data-testid="corp-signup-form">
            <div className="overline text-[#D4AF37]">Create Corporate Account</div>

            <Field label="Company Name" required>
              <input type="text" data-testid="corp-company-name" required minLength={2} value={f.company_name} onChange={onChange("company_name")} className="field-input" autoComplete="organization" placeholder="Acme Logistics LLC"/>
            </Field>

            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Your Name" required>
                <input type="text" data-testid="corp-contact-name" required minLength={2} value={f.contact_name} onChange={onChange("contact_name")} className="field-input" autoComplete="name" placeholder="Jane Smith"/>
              </Field>
              <Field label="Phone (optional)">
                <input type="tel" data-testid="corp-phone" value={f.phone} onChange={onChange("phone")} className="field-input" autoComplete="tel" placeholder="(414) 555-1234"/>
              </Field>
            </div>

            <Field label="Billing Email" required hint="We'll send your monthly invoice here.">
              <input type="email" data-testid="corp-email" required value={f.billing_email} onChange={onChange("billing_email")} className="field-input" autoComplete="email" placeholder="billing@acme.com"/>
            </Field>

            <Field label="Password" required hint="Minimum 8 characters. You'll use this to sign in.">
              <input type="password" data-testid="corp-password" required minLength={8} value={f.password} onChange={onChange("password")} className="field-input" autoComplete="new-password" placeholder="••••••••"/>
            </Field>

            <Field label="Billing Address (optional)">
              <input type="text" data-testid="corp-address" value={f.billing_address} onChange={onChange("billing_address")} className="field-input" autoComplete="street-address" placeholder="123 Main St, Milwaukee, WI 53202"/>
            </Field>

            <Field label="Anything we should know? (optional)">
              <textarea data-testid="corp-notes" rows={2} value={f.notes} onChange={onChange("notes")} className="field-input resize-none" placeholder="e.g. Expecting ~10 rides/month, mostly airport runs."/>
            </Field>

            {err && (
              <div data-testid="corp-signup-error" className="border border-red-500/40 bg-red-500/10 text-red-300 px-4 py-3 text-sm">
                {err}
              </div>
            )}

            <button type="submit" disabled={busy} data-testid="corp-submit" className="w-full bg-[#D4AF37] text-black py-4 font-semibold uppercase tracking-[0.24em] text-sm hover:bg-[#E5C158] disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2">
              {busy ? "Creating Account..." : <>Create Corporate Account <ArrowRight size={16}/></>}
            </button>

            <div className="text-xs text-[#71717A] text-center pt-2">
              Already have an account? <Link to="/login" data-testid="corp-existing-login" className="text-[#D4AF37] hover:underline">Sign in</Link>
            </div>
          </form>
        </section>
      </main>
    </div>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <div>
      <label className="field-label">
        {label}{required && <span className="text-[#D4AF37] ml-1">*</span>}
      </label>
      {children}
      {hint && <div className="text-xs text-[#71717A] mt-1.5">{hint}</div>}
    </div>
  );
}
